import { urlHostToConfigsMap } from "@/tenants/urlHostToConfigsMap.js"
import {
	applyCookieModeEndpoints,
	clearSessionActiveFlag,
	isCookieModeActive,
	isSessionActiveFlagSet
} from "@/modules/auth/cookieMode.js"
import store from "../store"

const consoleStyle = "padding: 1px 6px 1px 0px; color: #00be90; font-weight:700;"

function decideHostFromUrl(location) {
	const urlHost = location?.host
	const urlParams = new URLSearchParams(location?.search)

	// Let's say we have URL like this: https://www.some-app.com?hostOverride=some-other-app.com
	// Then we return host based on ?hostOverride: some-other-app.com
	if (urlParams.get?.("hostOverride")) return urlParams.get?.("hostOverride")

	if (localStorage.getItem(process.env.VUE_APP_HOST_KEY)) {
		return localStorage.getItem(process.env.VUE_APP_HOST_KEY)
	}

	// But if we have URL like this: https://www.some-app.com we return: www.some-app.com
	return urlHost
}

function initEnvConfig({ host }) {
	const envConfig = urlHostToConfigsMap[host]?.envConfig
	if (!envConfig) throw new Error("Couldn't load envConfig, did you forget to add envConfig?")

	// Save envConfig into global window so that it is easily accessible e.g. in API fetchers.
	// Shallow copy: envConfig values are SHARED module consts (COVERGO_SAAS_DEV is reused by
	// ~30 hosts), and cookie mode rewrites these keys — never mutate the const itself.
	window.envConfig = { ...envConfig }

	// CH-37826: cookie mode is per-host. RESOLVED_HOST records which map key we resolved,
	// so isCookieModeActive() can tell it apart from the real browser Host (see ?hostOverride).
	window.envConfig.COOKIE_MODE_ENABLED = urlHostToConfigsMap[host]?.cookieModeEnabled === true
	window.envConfig.RESOLVED_HOST = host

	// CH-52544: hosted login is a strict narrowing of cookie mode, so it is latched off the
	// RESOLVED value. isCookieModeActive() already folds in the host-mismatch and localhost
	// downgrades, so each of them takes the login screen back to the password form for free.
	window.envConfig.LOGIN_MODE =
		isCookieModeActive() && urlHostToConfigsMap[host]?.loginMode?.toLowerCase() === "codeflow" ? "CodeFlow" : "Ropc"

	// startCodeFlowLogin sets the session marker before the IdP hop, so a BFF bounce back
	// with ?error= means the marker is stale. Clear it before endpoints are picked from it.
	if (window.envConfig.LOGIN_MODE === "CodeFlow" && new URLSearchParams(window.location.search).has("error"))
		clearSessionActiveFlag()

	// Only rewrites when a session already exists (returning user / reload). A guest boot
	// deliberately stays on the direct gateway so /login is reachable without the gate.
	applyCookieModeEndpoints()

	// A host that gets flagged for cookie mode after someone logged in under the legacy
	// build keeps their tokens: clearTokens() below only fires when the resolved host
	// CHANGES, and it did not. Nothing new is sent with them, but a live bearer sitting in
	// JS-readable storage is the exact thing cookie mode exists to prevent, so drop it.
	//
	// Both gates matter. isCookieModeActive() rather than the raw COOKIE_MODE_ENABLED flag,
	// because the localhost and host-mismatch downgrades keep those sessions on the legacy
	// token flow — wiping their tokens would log them out on every reload. And only when no
	// session marker is set, because clearTokens() clears that marker too and would end a
	// live cookie session on every boot.
	if (isCookieModeActive() && !isSessionActiveFlagSet()) clearTokens()
}

function initAppConfig({ host }) {
	const appConfig = urlHostToConfigsMap[host]?.appConfig
	if (!appConfig) throw new Error("Couldn't load appConfig, did you forget to add appConfig?")

	// Log into console
	console.group(`%c Cover Studio initialized successfully, yay!`, consoleStyle)
	console.log(`%c host: ${host}`, consoleStyle)
	console.log(`%c tenantId: ${appConfig?.tenantId}`, consoleStyle)
	console.log(`%c appId: ${appConfig?.appId}`, consoleStyle)
	console.log(`%c GraphQL endpoint: ${window.envConfig?.COVERGO_GRAPHQL}`, consoleStyle)
	console.log(`%c Filesystem endpoint: ${window.envConfig?.COVERGO_FILE_SYSTEM}`, consoleStyle)
	console.groupEnd()

	const tenantId = appConfig?.tenantId
	const clientId = appConfig?.appId
	store.commit("updateTenantId", tenantId)
	store.commit("setClientId", clientId)
}

function initFeatureFlags({ host }) {
	const featureFlags = urlHostToConfigsMap[host]?.featureFlags || {}
	window.featureFlags = featureFlags
	store.commit("setFeatureFlags", featureFlags)
}

function clearTokens() {
	localStorage.removeItem(process.env.VUE_APP_TOKEN_KEY)
	// The refresh token has to go with it: it is minted for one tenant/client pair, so
	// leaving it behind lets a host switch replay it against the newly resolved tenant.
	localStorage.removeItem(process.env.VUE_APP_REFRESH_TOKEN_KEY)
	// The cookie-session marker is per-origin, so it survives a change of resolved host on
	// the same origin. Drop it with the tokens or the SPA thinks it still has a session.
	clearSessionActiveFlag()
	// The store is constructed at import time (main.js), so the auth state factory has
	// already read the token out of localStorage by the time this runs. Clearing storage
	// alone would leave that copy live in memory for the rest of the page.
	store.commit("auth/setToken", null)
}

export function initApp() {
	const host = decideHostFromUrl(window.location)
	if (!urlHostToConfigsMap[host]) return confirm(`This application doesn't exist.`)

	if (localStorage.getItem(process.env.VUE_APP_HOST_KEY) !== host) clearTokens()

	localStorage.setItem(process.env.VUE_APP_HOST_KEY, host)

	initEnvConfig({ host })
	initAppConfig({ host })
	initFeatureFlags({ host })
}
